import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Divider,
  Chip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Alert,
  IconButton,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Badge,
} from '@mui/material';
import {
  ArrowBackOutlined,
  PictureAsPdfOutlined,
  EditOutlined,
  CheckCircleOutline,
  ReceiptOutlined,
  PhotoCameraOutlined,
  WhatsApp,
  EmailOutlined,
  SendOutlined,
  CancelOutlined,
  Palette,
  StarOutline,
  Draw,
} from '@mui/icons-material';
import { useQuoteStore } from '../stores/useQuoteStore';
import { useClientStore } from '../stores/useClientStore';
import { useAuthStore } from '../stores/useAuthStore';
import { useFactureStore } from '../stores/useFactureStore';
import { StatutDevis, TypeLigneDevis, StatutFacture, Devis, LigneDevis } from '../types';
import { formatEuro, formatDate, calculateLigneTotal } from '../utils/calculations';
import devisService from '../services/devisService';
import emailService from '../services/emailService';
import SignaturePad from '../components/SignaturePad';
import { useNotification } from '../contexts/NotificationContext';

const DevisDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { id: idParam } = useParams<{ id: string }>();
  const { devis: allDevis, getDevisById, loadDevisById, updateDevis, saveSignature } = useQuoteStore();
  const { getClientById } = useClientStore();
  const { getFacturesByDevisId } = useFactureStore();
  const { user } = useAuthStore();
  const { showConfirm, showSuccess, showError } = useNotification();

  const [showSignature, setShowSignature] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSendingWhatsApp, setIsSendingWhatsApp] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [signatureDialogOpen, setSignatureDialogOpen] = useState(false);

  // Charger le devis au montage si nécessaire
  useEffect(() => {
    const loadDevis = async () => {
      if (idParam) {
        const id = parseInt(idParam, 10);

        // Vérifier dans le store local
        const foundDevis = getDevisById(id);

        // Si non trouvé, charger depuis l'API
        if (!foundDevis) {
          setIsLoading(true);
          await loadDevisById(id);
          setIsLoading(false);
        }
      }
    };

    loadDevis();
  }, [idParam, getDevisById, loadDevisById]);

  // Récupérer le devis depuis le store (source de vérité unique)
  // Utiliser allDevis pour que Zustand détecte les changements et déclenche un re-render
  const devis = idParam ? allDevis.find(d => d.id === parseInt(idParam, 10)) : undefined;

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Typography variant="h6" color="text.secondary">Chargement du devis...</Typography>
      </Box>
    );
  }

  if (!devis) {
    return (
      <Box>
        <Alert severity="error">Devis non trouvé</Alert>
        <Button onClick={() => navigate('/devis')} sx={{ mt: 2 }}>
          Retour à la liste
        </Button>
      </Box>
    );
  }

  // Récupérer le client depuis le store si disponible
  const client = getClientById(devis.clientId);

  // Récupérer les factures associées à ce devis
  const factures = getFacturesByDevisId(devis.id);

  // Utiliser le champ estFacture du backend si disponible
  // Sinon, fallback sur le calcul côté frontend (pour compatibilité)
  const devisCompletementFacture = devis.estFacture ?? factures.some(
    f => f.type === 'COMPLETE' || f.type === 'SOLDE'
  );

  // Si le client n'est pas dans le store mais qu'on a clientNom, créer un objet client minimal
  const clientToDisplay = client || (devis.clientNom ? {
    id: devis.clientId,
    nom: devis.clientNom,
    prenom: '',
    email: '',
    telephone: '',
    adresse: '',
    codePostal: '',
    ville: '',
    dateCreation: '',
    dateModification: '',
  } : null);

  if (!clientToDisplay) {
    return (
      <Box>
        <Alert severity="error">Client non trouvé</Alert>
        <Button onClick={() => navigate('/devis')} sx={{ mt: 2 }}>
          Retour à la liste
        </Button>
      </Box>
    );
  }

  const handleSaveSignature = async (signatureData: string) => {
    if (!devis) return;

    const dateSignature = new Date().toISOString();

    console.log('📝 Sauvegarde de la signature:', {
      devisId: devis.id,
      hasSignature: !!signatureData,
      signatureLength: signatureData.length,
      dateSignature,
    });

    try {
      // Utiliser la méthode dédiée à la signature
      await saveSignature(devis.id, signatureData, dateSignature);
      console.log('✅ Signature sauvegardée, fermeture du pad');
      setShowSignature(false);
    } catch (error) {
      console.error('❌ Erreur lors de la sauvegarde de la signature:', error);
      showError('Erreur lors de la sauvegarde de la signature. Veuillez réessayer.');
    }
  };

  const handleDownloadPDF = async () => {
    try {
      setIsLoading(true);
      // Télécharger le PDF depuis le backend
      const pdfBlob = await devisService.downloadPDF(devis.id);

      // Créer un lien de téléchargement
      const url = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Devis_${devis.numero}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erreur lors du téléchargement du PDF:', error);
      showError('Erreur lors du téléchargement du PDF. Veuillez réessayer.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleShareWhatsApp = async () => {
    if (!user) {
      showError('Les informations utilisateur ne sont pas encore chargées. Veuillez réessayer dans quelques secondes.');
      return;
    }
    if (!clientToDisplay.telephone) {
      showError('Numéro de téléphone du client non disponible');
      return;
    }

    setIsSendingWhatsApp(true);
    try {
      // Récupérer le PDF en base64 depuis le backend
      const pdfBase64 = await devisService.getPDFBase64(devis.id);

      const message = `Bonjour ${clientToDisplay.prenom || clientToDisplay.nom},\n\nVoici votre devis ${devis.numero} d'un montant de ${formatEuro(devis.totalTTC)}.\n\nValable jusqu'au ${formatDate(devis.dateValidite)}\n\nCordialement,\n${user.entreprise}`;
      const phoneNumber = clientToDisplay.telephone.replace(/\s/g, '');

      try {
        // Essayer d'envoyer via l'API backend
        await emailService.sendWhatsApp({
          phoneNumber,
          message,
          pdfBase64,
          filename: `Devis_${devis.numero}.pdf`,
        });
        showSuccess('Message WhatsApp envoyé avec succès !');
      } catch (apiError) {
        // Si l'API échoue, fallback sur le lien WhatsApp classique avec téléchargement du PDF
        console.warn('API WhatsApp non disponible, utilisation du fallback');

        // Télécharger le PDF localement
        const link = document.createElement('a');
        link.href = `data:application/pdf;base64,${pdfBase64}`;
        link.download = `Devis_${devis.numero}.pdf`;
        link.click();

        // Ouvrir WhatsApp
        setTimeout(() => {
          const url = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
          window.open(url, '_blank');
          showSuccess('Le PDF a été téléchargé. Sur WhatsApp, cliquez sur l\'icône de trombone (📎) pour joindre le fichier.');
        }, 500);
      }
    } catch (error) {
      console.error('Erreur lors de l\'envoi WhatsApp:', error);
      showError('Erreur lors de l\'envoi du message WhatsApp');
    } finally {
      setIsSendingWhatsApp(false);
    }
  };

  const handleShareEmail = async () => {
    if (!user) {
      showError('Les informations utilisateur ne sont pas encore chargées. Veuillez réessayer dans quelques secondes.');
      return;
    }
    if (!clientToDisplay.email) {
      showError('Email du client non disponible');
      return;
    }

    setIsSendingEmail(true);
    try {
      // Récupérer le PDF en base64 depuis le backend
      const pdfBase64 = await devisService.getPDFBase64(devis.id);

      const subject = `Devis ${devis.numero} - ${user.entreprise}`;

      // Envoyer via l'API backend (le body sera généré automatiquement côté backend)
      await emailService.sendEmail({
        to: clientToDisplay.email,
        subject,
        pdfBase64,
        filename: `Devis_${devis.numero}.pdf`,
        fromEmail: user.emailProfessionnel || user.email,
        fromName: user.prenom && user.nom ? `${user.prenom} ${user.nom}` : user.nom,
        // Données pour génération automatique du body (DEVIS)
        clientPrenom: clientToDisplay.prenom,
        clientNom: clientToDisplay.nom,
        devisNumero: devis.numero,
        devisMontantTTC: devis.totalTTC,
        devisDateValidite: devis.dateValidite,
        entreprise: user.entreprise,
      });

      showSuccess('Email envoyé avec succès !');
    } catch (error: any) {
      console.error('Erreur lors de l\'envoi de l\'email:', error);
      showError(error.message || 'Erreur lors de l\'envoi de l\'email. Vérifiez la configuration du serveur.');
    } finally {
      setIsSendingEmail(false);
    }
  };

  const handleMarkAsEnvoye = async () => {
    if (!devis) return;

    try {
      await updateDevis(devis.id, {
        ...devis,
        statut: StatutDevis.ENVOYE,
      });
    } catch (error: any) {
      console.error('Erreur lors de la mise à jour du devis:', error);

      // L'intercepteur axios gère déjà les erreurs 401/403 (session expirée)
      // On affiche un message uniquement pour les autres erreurs
      if (error.response?.status !== 401 && error.response?.status !== 403) {
        showError('Erreur lors de la mise à jour du devis. Veuillez réessayer.');
      }
    }
  };

  const handleMarkAsRefuse = async () => {
    if (!devis) return;

    const confirmed = await showConfirm({
      title: 'Marquer comme refusé',
      message: 'Êtes-vous sûr de vouloir marquer ce devis comme refusé ?',
      confirmText: 'Marquer comme refusé',
      cancelText: 'Annuler',
      severity: 'warning',
    });

    if (!confirmed) return;

    try {
      await updateDevis(devis.id, {
        ...devis,
        statut: StatutDevis.REFUSE,
      });
    } catch (error: any) {
      console.error('Erreur lors de la mise à jour du devis:', error);

      // L'intercepteur axios gère déjà les erreurs 401/403 (session expirée)
      // On affiche un message uniquement pour les autres erreurs
      if (error.response?.status !== 401 && error.response?.status !== 403) {
        showError('Erreur lors de la mise à jour du devis. Veuillez réessayer.');
      }
    }
  };

  const handleMarkAsSigne = async () => {
    if (!devis) return;

    const confirmed = await showConfirm({
      title: 'Marquer comme signé',
      message: 'Êtes-vous sûr de vouloir marquer ce devis comme signé ? Cette action indique que le client a accepté le devis.',
      confirmText: 'Marquer comme signé',
      cancelText: 'Annuler',
      severity: 'info',
    });

    if (!confirmed) return;

    try {
      const dateSignature = new Date().toISOString();

      await updateDevis(devis.id, {
        ...devis,
        statut: StatutDevis.ACCEPTE,
        dateSignature,
      });
    } catch (error: any) {
      console.error('Erreur lors de la mise à jour du devis:', error);

      // L'intercepteur axios gère déjà les erreurs 401/403 (session expirée)
      // On affiche un message uniquement pour les autres erreurs
      if (error.response?.status !== 401 && error.response?.status !== 403) {
        showError('Erreur lors de la mise à jour du devis. Veuillez réessayer.');
      }
    }
  };

  const getStatutColor = (statut: StatutDevis) => {
    switch (statut) {
      case StatutDevis.BROUILLON:
        return 'default';
      case StatutDevis.ENVOYE:
        return 'primary';
      case StatutDevis.ACCEPTE:
        return 'success';
      case StatutDevis.REFUSE:
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#333' }}>
          Devis {devis.numero}
        </Typography>
        <Button
          startIcon={<ArrowBackOutlined />}
          onClick={() => navigate('/devis')}
          variant="outlined"
        >
          Retour
        </Button>
      </Box>

      {/* Statut et actions */}
      <Paper sx={{ p: 3, mb: 3, boxShadow: 2, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, gap: 2 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <Chip
              label={devis.statut}
              color={getStatutColor(devis.statut)}
              sx={{ fontSize: '1rem', fontWeight: 600, px: 2, py: 0.5 }}
            />
            {devis.signatureClient && devis.dateSignature && (
              <Chip
                icon={<CheckCircleOutline />}
                label={`Signé le ${formatDate(devis.dateSignature)}`}
                color="success"
                sx={{ fontSize: '1rem', fontWeight: 600, px: 2, py: 0.5 }}
              />
            )}
            {devisCompletementFacture && (
              <Chip
                icon={<ReceiptOutlined />}
                label="Facturé"
                sx={{
                  fontSize: '1rem',
                  fontWeight: 600,
                  px: 2,
                  py: 0.5,
                  bgcolor: '#F59E0B',
                  color: 'white',
                }}
              />
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', width: { xs: '100%', md: 'auto' } }}>
            {/* Boutons pour devis BROUILLON */}
            {devis.statut === StatutDevis.BROUILLON && (
              <>
                <Button
                  variant="outlined"
                  startIcon={<EditOutlined />}
                  onClick={() => navigate(`/devis/edit/${devis.id}`)}
                  fullWidth
                  sx={{ flex: { xs: '1 1 100%', sm: '0 1 auto' } }}
                >
                  Modifier
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<Palette />}
                  onClick={() => navigate('/customization')}
                  fullWidth
                  sx={{
                    borderColor: '#9333ea',
                    color: '#9333ea',
                    '&:hover': {
                      borderColor: '#7c3aed',
                      bgcolor: '#faf5ff',
                    },
                    flex: { xs: '1 1 100%', sm: '0 1 auto' },
                  }}
                >
                  Personnaliser
                </Button>
                <Button
                  variant="contained"
                  startIcon={<PictureAsPdfOutlined />}
                  onClick={handleDownloadPDF}
                  fullWidth
                  sx={{
                    bgcolor: '#374151',
                    '&:hover': { bgcolor: '#1F2937' },
                    flex: { xs: '1 1 100%', sm: '0 1 auto' },
                  }}
                >
                  Télécharger Devis
                </Button>
                <Box sx={{ display: 'flex', gap: 2, flex: { xs: '1 1 100%', sm: '0 1 auto' } }}>
                  <Tooltip title="Envoyer par WhatsApp">
                    <IconButton
                      onClick={handleShareWhatsApp}
                      disabled={isSendingWhatsApp}
                      sx={{
                        bgcolor: '#25D366',
                        color: 'white',
                        '&:hover': { bgcolor: '#1da851' },
                        '&.Mui-disabled': {
                          bgcolor: '#25D36680',
                          color: 'white',
                        },
                      }}
                    >
                      {isSendingWhatsApp ? <CircularProgress size={24} color="inherit" /> : <WhatsApp />}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Envoyer par email">
                    <IconButton
                      onClick={handleShareEmail}
                      disabled={isSendingEmail}
                      sx={{
                        bgcolor: '#374151',
                        color: 'white',
                        '&:hover': { bgcolor: '#1F2937' },
                        '&.Mui-disabled': {
                          bgcolor: '#4B5563',
                          color: 'white',
                        },
                      }}
                    >
                      {isSendingEmail ? <CircularProgress size={24} color="inherit" /> : <EmailOutlined />}
                    </IconButton>
                  </Tooltip>
                </Box>
                <Button
                  variant="contained"
                  startIcon={<SendOutlined />}
                  onClick={handleMarkAsEnvoye}
                  fullWidth
                  sx={{
                    bgcolor: '#10B981',
                    '&:hover': { bgcolor: '#059669' },
                    flex: { xs: '1 1 100%', sm: '0 1 auto' },
                  }}
                >
                  Marquer comme envoyé
                </Button>
              </>
            )}

            {/* Boutons pour devis ENVOYE */}
            {devis.statut === StatutDevis.ENVOYE && (
              <>
                {/* Modification uniquement pour ENVOYE non signé */}
                {!devis.signatureClient && (
                  <Button
                    variant="outlined"
                    startIcon={<EditOutlined />}
                    onClick={() => navigate(`/devis/edit/${devis.id}`)}
                    fullWidth
                    sx={{ flex: { xs: '1 1 100%', sm: '0 1 auto' } }}
                  >
                    Modifier
                  </Button>
                )}

                {/* Photos et factures uniquement pour devis signés */}
                {devis.signatureClient && (
                  <>
                    <Button
                      variant="contained"
                      startIcon={<PhotoCameraOutlined />}
                      onClick={() => navigate(`/photos/${devis.id}`)}
                      fullWidth
                      sx={{
                        bgcolor: '#F59E0B',
                        '&:hover': { bgcolor: '#D97706' },
                        flex: { xs: '1 1 100%', sm: '0 1 auto' },
                      }}
                    >
                      Photos chantier
                    </Button>
                    {!devisCompletementFacture && (
                      <Button
                        variant="contained"
                        startIcon={<ReceiptOutlined />}
                        onClick={() => navigate(`/factures/new/${devis.id}`)}
                        fullWidth
                        sx={{
                          bgcolor: '#374151',
                          '&:hover': { bgcolor: '#1F2937' },
                          flex: { xs: '1 1 100%', sm: '0 1 auto' },
                        }}
                      >
                        Créer facture
                      </Button>
                    )}
                  </>
                )}

                <Button
                  variant="outlined"
                  startIcon={<Palette />}
                  onClick={() => navigate('/customization')}
                  fullWidth
                  sx={{
                    borderColor: '#9333ea',
                    color: '#9333ea',
                    '&:hover': {
                      borderColor: '#7c3aed',
                      bgcolor: '#faf5ff',
                    },
                    flex: { xs: '1 1 100%', sm: '0 1 auto' },
                  }}
                >
                  Personnaliser
                </Button>
                <Button
                  variant="contained"
                  startIcon={<PictureAsPdfOutlined />}
                  onClick={handleDownloadPDF}
                  fullWidth
                  sx={{
                    bgcolor: '#374151',
                    '&:hover': { bgcolor: '#1F2937' },
                    flex: { xs: '1 1 100%', sm: '0 1 auto' },
                  }}
                >
                  Télécharger Devis
                </Button>
                <Box sx={{ display: 'flex', gap: 2, flex: { xs: '1 1 100%', sm: '0 1 auto' } }}>
                  <Tooltip title="Envoyer par WhatsApp">
                    <IconButton
                      onClick={handleShareWhatsApp}
                      disabled={isSendingWhatsApp}
                      sx={{
                        bgcolor: '#25D366',
                        color: 'white',
                        '&:hover': { bgcolor: '#1da851' },
                        '&.Mui-disabled': {
                          bgcolor: '#25D36680',
                          color: 'white',
                        },
                      }}
                    >
                      {isSendingWhatsApp ? <CircularProgress size={24} color="inherit" /> : <WhatsApp />}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Envoyer par email">
                    <IconButton
                      onClick={handleShareEmail}
                      disabled={isSendingEmail}
                      sx={{
                        bgcolor: '#374151',
                        color: 'white',
                        '&:hover': { bgcolor: '#1F2937' },
                        '&.Mui-disabled': {
                          bgcolor: '#4B5563',
                          color: 'white',
                        },
                      }}
                    >
                      {isSendingEmail ? <CircularProgress size={24} color="inherit" /> : <EmailOutlined />}
                    </IconButton>
                  </Tooltip>
                </Box>
                {/* Bouton "Marquer comme signé" pour utilisateurs ESSENTIEL (sans signature électronique) */}
                {!devis.signatureClient && user?.subscriptionTier !== 'PREMIUM' && (
                  <Button
                    variant="contained"
                    startIcon={<CheckCircleOutline />}
                    onClick={handleMarkAsSigne}
                    fullWidth
                    sx={{
                      bgcolor: '#10B981',
                      '&:hover': { bgcolor: '#059669' },
                      flex: { xs: '1 1 100%', sm: '0 1 auto' },
                    }}
                  >
                    Marquer comme signé
                  </Button>
                )}
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<CancelOutlined />}
                  onClick={handleMarkAsRefuse}
                  fullWidth
                  sx={{
                    borderWidth: 2,
                    '&:hover': {
                      borderWidth: 2,
                      bgcolor: 'error.light',
                      color: 'white',
                    },
                    flex: { xs: '1 1 100%', sm: '0 1 auto' },
                  }}
                >
                  Marquer comme refusé
                </Button>
              </>
            )}

            {/* Boutons pour devis ACCEPTE ou REFUSE */}
            {(devis.statut === StatutDevis.ACCEPTE || devis.statut === StatutDevis.REFUSE) && (
              <>
                {/* Photos et factures uniquement pour devis acceptés */}
                {devis.statut === StatutDevis.ACCEPTE && (
                  <>
                    <Button
                      variant="contained"
                      startIcon={<PhotoCameraOutlined />}
                      onClick={() => navigate(`/photos/${devis.id}`)}
                      fullWidth
                      sx={{
                        bgcolor: '#F59E0B',
                        '&:hover': { bgcolor: '#D97706' },
                        flex: { xs: '1 1 100%', sm: '0 1 auto' },
                      }}
                    >
                      Photos chantier
                    </Button>
                    {!devisCompletementFacture && (
                      <Button
                        variant="contained"
                        startIcon={<ReceiptOutlined />}
                        onClick={() => navigate(`/factures/new/${devis.id}`)}
                        fullWidth
                        sx={{
                          bgcolor: '#374151',
                          '&:hover': { bgcolor: '#1F2937' },
                          flex: { xs: '1 1 100%', sm: '0 1 auto' },
                        }}
                      >
                        Créer facture
                      </Button>
                    )}
                  </>
                )}

                <Button
                  variant="outlined"
                  startIcon={<Palette />}
                  onClick={() => navigate('/customization')}
                  fullWidth
                  sx={{
                    borderColor: '#9333ea',
                    color: '#9333ea',
                    '&:hover': {
                      borderColor: '#7c3aed',
                      bgcolor: '#faf5ff',
                    },
                    flex: { xs: '1 1 100%', sm: '0 1 auto' },
                  }}
                >
                  Personnaliser
                </Button>
                <Button
                  variant="contained"
                  startIcon={<PictureAsPdfOutlined />}
                  onClick={handleDownloadPDF}
                  fullWidth
                  sx={{
                    bgcolor: '#374151',
                    '&:hover': { bgcolor: '#1F2937' },
                    flex: { xs: '1 1 100%', sm: '0 1 auto' },
                  }}
                >
                  Télécharger Devis
                </Button>
              </>
            )}
          </Box>
        </Box>
      </Paper>

      {/* Informations client et devis */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, boxShadow: 2, borderRadius: 2, height: '100%' }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'theme.palette.primary.main', mb: 2 }}>
              Client
            </Typography>
            <Typography variant="body1" sx={{ mb: 1 }}>
              <strong>
                {clientToDisplay.prenom} {clientToDisplay.nom}
              </strong>
            </Typography>
            {clientToDisplay.adresse && (
              <Typography variant="body2" color="text.secondary">
                {clientToDisplay.adresse}
              </Typography>
            )}
            {(clientToDisplay.codePostal || clientToDisplay.ville) && (
              <Typography variant="body2" color="text.secondary">
                {clientToDisplay.codePostal} {clientToDisplay.ville}
              </Typography>
            )}
            {clientToDisplay.telephone && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Tél: {clientToDisplay.telephone}
              </Typography>
            )}
            {clientToDisplay.email && (
              <Typography variant="body2" color="text.secondary">
                Email: {clientToDisplay.email}
              </Typography>
            )}
            {!client && (
              <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 2 }}>
                ⚠️ Informations partielles - Rechargez la page pour voir les détails complets
              </Typography>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, boxShadow: 2, borderRadius: 2, height: '100%' }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'theme.palette.primary.main', mb: 2 }}>
              Informations du devis
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">Numéro:</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{devis.numero}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">Date de création:</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatDate(devis.dateEmission || devis.dateCreation || '')}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">Date de validité:</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatDate(devis.dateValidite)}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">Taux TVA:</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{devis.tauxTVA}%</Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Lignes du devis */}
      <Paper sx={{ p: 3, mb: 3, boxShadow: 2, borderRadius: 2 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'theme.palette.primary.main', mb: 2 }}>
          Détails
        </Typography>

        {/* Version mobile - Cartes */}
        <Box sx={{ display: { xs: 'block', md: 'none' } }}>
          {devis.lignes.map((ligne: LigneDevis, index: number) => {
            let quantiteDisplay = '';

            if (ligne.type === TypeLigneDevis.SURFACE) {
              if (ligne.modeSaisieSurface === 'dimensions' && ligne.longueur && ligne.largeur) {
                const surface = Number(ligne.longueur) * Number(ligne.largeur);
                const surfaceFinale = ligne.avecPertes ? surface * 1.1 : surface;
                quantiteDisplay = `${surfaceFinale.toFixed(2)} m²`;
              } else if (ligne.modeSaisieSurface === 'direct' && ligne.surface) {
                const surfaceFinale = ligne.avecPertes ? Number(ligne.surface) * 1.1 : Number(ligne.surface);
                quantiteDisplay = `${surfaceFinale.toFixed(2)} m²`;
              }
            } else if (ligne.type === TypeLigneDevis.UNITAIRE) {
              quantiteDisplay = `${ligne.quantite}`;
            } else {
              quantiteDisplay = '1';
            }

            return (
              <React.Fragment key={index}>
                <Paper
                  elevation={1}
                  sx={{
                    p: 2,
                    mb: 2,
                    bgcolor: index % 2 === 0 ? '#fafafa' : 'white',
                    borderLeft: '4px solid theme.palette.primary.main',
                  }}
                >
                  <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                    {ligne.description}
                  </Typography>

                  {ligne.type === TypeLigneDevis.SURFACE && (
                    <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 1 }}>
                      {ligne.modeSaisieSurface === 'dimensions' && ligne.longueur && ligne.largeur
                        ? `${ligne.longueur}m × ${ligne.largeur}m${ligne.avecPertes ? ' + 10% pertes' : ''}`
                        : ligne.modeSaisieSurface === 'direct' && ligne.surface
                        ? `Surface: ${ligne.surface}m²${ligne.avecPertes ? ' + 10% pertes' : ''}`
                        : ''}
                    </Typography>
                  )}
                  {ligne.type === TypeLigneDevis.UNITAIRE && (
                    <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 1 }}>
                      Mètre linéaire: {ligne.quantite}ml
                    </Typography>
                  )}

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Quantité: <strong>{quantiteDisplay}</strong>
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      P.U: <strong>{formatEuro(ligne.prixUnitaire)}</strong>
                    </Typography>
                  </Box>

                  <Divider sx={{ my: 1 }} />

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>Total HT:</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'theme.palette.primary.main' }}>
                      {formatEuro(calculateLigneTotal(ligne))}
                    </Typography>
                  </Box>
                </Paper>

                {/* Sous-lignes (consommables) */}
                {ligne.sousLignes && ligne.sousLignes.length > 0 && (
                  <Box sx={{ ml: 2, mb: 2 }}>
                    {ligne.sousLignes.map((sousLigne, sousIndex) => (
                      <Paper
                        key={`sous-${index}-${sousIndex}`}
                        elevation={0}
                        sx={{
                          p: 1.5,
                          mb: 1,
                          bgcolor: '#f5f5f5',
                          borderLeft: '3px solid #2196f3',
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5, color: '#666' }}>
                          → {sousLigne.description}
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">
                            {sousLigne.quantite} kg
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            P.U: {formatEuro(sousLigne.prixUnitaire)}
                          </Typography>
                          <Typography variant="caption" sx={{ fontWeight: 600, color: '#2196f3' }}>
                            {formatEuro(calculateLigneTotal(sousLigne))}
                          </Typography>
                        </Box>
                      </Paper>
                    ))}
                  </Box>
                )}
              </React.Fragment>
            );
          })}
        </Box>

        {/* Version desktop - Tableau */}
        <Box sx={{ display: { xs: 'none', md: 'block' }, overflowX: 'auto', width: '100%' }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 600 }}>Désignation</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>Quantité</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Prix unitaire</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Total HT</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {devis.lignes.map((ligne: LigneDevis, index: number) => {
                let quantiteDisplay = '';

                if (ligne.type === TypeLigneDevis.SURFACE) {
                  if (ligne.modeSaisieSurface === 'dimensions' && ligne.longueur && ligne.largeur) {
                    const surface = Number(ligne.longueur) * Number(ligne.largeur);
                    const surfaceFinale = ligne.avecPertes ? surface * 1.1 : surface;
                    quantiteDisplay = `${surfaceFinale.toFixed(2)} m²`;
                  } else if (ligne.modeSaisieSurface === 'direct' && ligne.surface) {
                    const surfaceFinale = ligne.avecPertes ? Number(ligne.surface) * 1.1 : Number(ligne.surface);
                    quantiteDisplay = `${surfaceFinale.toFixed(2)} m²`;
                  }
                } else if (ligne.type === TypeLigneDevis.UNITAIRE) {
                  quantiteDisplay = `${ligne.quantite}`;
                } else {
                  quantiteDisplay = '1';
                }

                return (
                  <React.Fragment key={index}>
                    <TableRow>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {ligne.description}
                        </Typography>
                        {ligne.type === TypeLigneDevis.SURFACE && (
                          <Typography variant="caption" color="text.secondary">
                            {ligne.modeSaisieSurface === 'dimensions' && ligne.longueur && ligne.largeur
                              ? `${ligne.longueur}m × ${ligne.largeur}m${ligne.avecPertes ? ' + 10% pertes' : ''}`
                              : ligne.modeSaisieSurface === 'direct' && ligne.surface
                              ? `Surface: ${ligne.surface}m²${ligne.avecPertes ? ' + 10% pertes' : ''}`
                              : ''}
                          </Typography>
                        )}
                        {ligne.type === TypeLigneDevis.UNITAIRE && (
                          <Typography variant="caption" color="text.secondary">
                            Mètre linéaire: {ligne.quantite}ml
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">{quantiteDisplay}</TableCell>
                      <TableCell align="right">{formatEuro(ligne.prixUnitaire)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        {formatEuro(calculateLigneTotal(ligne))}
                      </TableCell>
                    </TableRow>

                    {/* Sous-lignes (consommables) */}
                    {ligne.sousLignes && ligne.sousLignes.length > 0 && ligne.sousLignes.map((sousLigne, sousIndex) => (
                      <TableRow key={`sous-${index}-${sousIndex}`} sx={{ bgcolor: '#f5f5f5' }}>
                        <TableCell sx={{ pl: 4 }}>
                          <Typography variant="body2" sx={{ fontWeight: 400, color: '#666' }}>
                            → {sousLigne.description}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" sx={{ color: '#666' }}>
                            {sousLigne.quantite} kg
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ color: '#666' }}>
                            {formatEuro(sousLigne.prixUnitaire)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ fontWeight: 500, color: '#2196f3' }}>
                            {formatEuro(calculateLigneTotal(sousLigne))}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Box sx={{ width: { xs: '100%', sm: '400px' } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body1">Sous-total HT:</Typography>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {formatEuro(devis.montantHT || devis.sousTotal)}
              </Typography>
            </Box>

            {/* Afficher la remise si elle existe */}
            {devis.typeRemise && devis.valeurRemise && devis.valeurRemise > 0 && (
              <>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body1" color="error">
                    Remise ({devis.typeRemise === 'POURCENTAGE' ? `${devis.valeurRemise}%` : 'montant fixe'}):
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, color: 'error.main' }}>
                    - {formatEuro(devis.montantRemise || 0)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>Montant HT après remise:</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 700 }}>
                    {formatEuro(devis.montantHTApresRemise || (devis.montantHT || devis.sousTotal))}
                  </Typography>
                </Box>
              </>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body1">TVA ({devis.tauxTVA}%):</Typography>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {formatEuro(devis.montantTVA)}
              </Typography>
            </Box>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                TOTAL TTC:
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#4CAF50' }}>
                {formatEuro(devis.totalTTC)}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Notes et conditions */}
      {(devis.notes || devis.conditions) && (
        <Paper sx={{ p: 3, mb: 3, boxShadow: 2, borderRadius: 2 }}>
          {devis.notes && (
            <>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'theme.palette.primary.main' }}>
                Notes
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>
                {devis.notes}
              </Typography>
            </>
          )}
          {devis.conditions && (
            <>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'theme.palette.primary.main' }}>
                Conditions
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                {devis.conditions}
              </Typography>
            </>
          )}
        </Paper>
      )}

      {/* Factures associées */}
      {factures.length > 0 && (
        <Paper sx={{ p: 3, mb: 3, boxShadow: 2, borderRadius: 2 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'theme.palette.primary.main', mb: 2 }}>
            Factures associées
          </Typography>
          <Box sx={{ overflowX: 'auto', width: '100%' }}>
            <Table sx={{ minWidth: { xs: 650, sm: 'auto' } }}>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Numéro</TableCell>
                  <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Montant</TableCell>
                  <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Statut</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {factures.map((facture) => (
                  <TableRow key={facture.id} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {facture.numero}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Typography variant="body2">
                        {facture.type === 'ACOMPTE' ? `Acompte (${facture.pourcentageAcompte}%)` :
                         facture.type === 'SOLDE' ? 'Solde' : 'Complète'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Typography variant="body2">
                        {formatDate(facture.dateEmission)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {formatEuro(facture.montantTTC)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Chip
                        label={facture.statut.replace('_', ' ')}
                        color={facture.statut === StatutFacture.PAYEE ? 'success' :
                               facture.statut === StatutFacture.EN_ATTENTE ? 'warning' : 'error'}
                        size="small"
                        sx={{ fontWeight: 600 }}
                      />
                    </TableCell>
                    <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => navigate(`/factures/${facture.id}`)}
                      >
                        Voir
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Paper>
      )}

      {/* Section signature - Fonctionnalité Premium */}
      {!devis.signatureClient && devis.statut == StatutDevis.ENVOYE && (
        <>
          {user?.subscriptionTier === 'PREMIUM' ? (
            <Paper sx={{ p: 3, mb: 3, boxShadow: 2, borderRadius: 2 }}>
              {!showSignature ? (
                <Box sx={{ textAlign: 'center', py: 3 }}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'theme.palette.primary.main' }}>
                    Ce devis n'est pas encore signé
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Pour accepter ce devis, veuillez le signer ci-dessous
                  </Typography>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={() => setShowSignature(true)}
                    sx={{
                      px: 4,
                      py: 1.5,
                      background: 'linear-gradient(135deg, theme.palette.primary.main 0%, ${theme.palette.secondary.main} 100%)',
                    }}
                  >
                    Signer le devis
                  </Button>
                </Box>
              ) : (
                <SignaturePad
                  onSave={handleSaveSignature}
                  onClear={() => setShowSignature(false)}
                  label="Signature du client"
                />
              )}
            </Paper>
          ) : (
            <Paper sx={{ p: 3, mb: 3, boxShadow: 2, borderRadius: 2 }}>
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'theme.palette.primary.main' }}>
                  Ce devis n'est pas encore signé
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Pour accepter ce devis, veuillez le signer
                </Typography>
                <Tooltip title="Fonctionnalité Premium - Cliquez pour en savoir plus">
                  <Button
                    variant="contained"
                    size="large"
                    onClick={() => setSignatureDialogOpen(true)}
                    startIcon={
                      <Badge
                        badgeContent={<StarOutline sx={{ fontSize: 14 }} />}
                        sx={{
                          '& .MuiBadge-badge': {
                            backgroundColor: '#F59E0B',
                            color: 'white',
                            right: -6,
                            top: -6,
                            border: '2px solid white',
                          },
                        }}
                      >
                        <Draw />
                      </Badge>
                    }
                    sx={{
                      px: 4,
                      py: 1.5,
                    }}
                  >
                    Signer le devis
                  </Button>
                </Tooltip>
              </Box>
            </Paper>
          )}
        </>
      )}

      {/* Signature existante */}
      {devis.signatureClient && (
        <Paper sx={{ p: 3, mb: 3, boxShadow: 2, borderRadius: 2 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#4CAF50', mb: 2 }}>
            ✅ Signature du client
          </Typography>
          <Box
            sx={{
              border: '2px solid #4CAF50',
              borderRadius: 2,
              p: 2,
              bgcolor: '#f1f8f4',
              textAlign: 'center',
            }}
          >
            <img
              src={devis.signatureClient}
              alt="Signature"
              style={{ maxWidth: '100%', height: 'auto', maxHeight: '200px' }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
              Signé le {formatDate(devis.dateSignature || '')}
            </Typography>
          </Box>
        </Paper>
      )}

      {/* Dialog Premium - Signature électronique */}
      <Dialog
        open={signatureDialogOpen}
        onClose={() => setSignatureDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600, color: '#F59E0B' }}>
          ⭐ Signature électronique à distance - Fonctionnalité Premium
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            La signature électronique à distance est une fonctionnalité exclusive de l'offre <strong>PREMIUM</strong>.
          </Typography>
          <Typography variant="body2" paragraph>
            <strong>Avantages de la signature électronique :</strong>
          </Typography>
          <Box component="ul" sx={{ pl: 2 }}>
            <li>
              <Typography variant="body2">
                ✅ <strong>Signature à distance</strong> : Vos clients signent leurs devis en ligne, où qu'ils soient
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                ✅ <strong>Validation instantanée</strong> : Le devis est automatiquement validé dès la signature
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                ✅ <strong>Sécurité juridique</strong> : Signature horodatée avec valeur légale
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                ✅ <strong>Gain de temps</strong> : Plus besoin d'imprimer, scanner ou se déplacer
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                ✅ <strong>Traçabilité complète</strong> : Historique de toutes les signatures avec date et heure
              </Typography>
            </li>
          </Box>
          <Typography variant="body2" sx={{ mt: 2, fontStyle: 'italic', color: 'text.secondary' }}>
            Passez à l'offre Premium pour bénéficier de cette fonctionnalité et accélérer vos processus de validation.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSignatureDialogOpen(false)} color="inherit">
            Fermer
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setSignatureDialogOpen(false);
              navigate('/subscription');
            }}
            sx={{
              background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
              color: 'white',
            }}
          >
            Voir l'offre Premium
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DevisDetailPage;
